import React, { useMemo, useState } from 'react';

import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  Table,
  TableColumn,
  Typography,
  Modal,
  TextField,
  Button,
  SelectMenu,
} from '@scale/scaleui';
import {
  StageDefinitionInfo,
  SFUBatchTableDto,
  SFUBatchInfo,
  SFUBatchRow,
} from '@scale/shared-types';

interface TableBatchRow {
  stages: Record<string, number>;
  batchInfo: SFUBatchInfo;
  tasks: number;
  hold: number;
  tasksCompleted: number;
}

type SfuBatchStatusPanelProps = {
  batchOverview?: SFUBatchTableDto;
};

const SfuBatchStatusPanel = ({ batchOverview }: SfuBatchStatusPanelProps) => {
  const { isRemoAdmin } = useRemoAdminCtx();

  const [isBatchPriorityModalOpen, setIsBatchPriorityModalOpen] = useState<boolean>(false);
  const [priorityLevelForUpdate, setPriorityLevelForUpdate] = useState<number>(0);
  const [isForcingInheritance, setIsForcingInheritance] = useState<boolean>(false);
  const [batchIdForUpdate, setBatchIdForUpdate] = useState<string>('');

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return '/remoadmin';
    } else {
      return '/corp';
    }
  }, [isRemoAdmin]);

  // Table Generation Helpers
  function generateStageColumn(
    stageDefinitionId: string,
    stageDefinitionName: string,
  ): TableColumn {
    return {
      id: stageDefinitionId,
      header: `Stage: ${stageDefinitionName} Completed`,
      cell: function stageCell(props: {
        row?: { original?: { stages?: Record<string, number> } };
      }) {
        const completedCount = props?.row?.original?.stages?.[stageDefinitionId];
        return <div>{completedCount}</div>;
      },
    };
  }

  function formatBatchRow(batchRowDto: SFUBatchRow): TableBatchRow {
    const formattedBatchRow: TableBatchRow = {
      stages: batchRowDto.completedStagesCounts,
      batchInfo: batchRowDto.batchInfo,
      tasks: batchRowDto.taskCount,
      hold: batchRowDto.holdTasksCount,
      tasksCompleted: batchRowDto.completedTasksCount,
    };
    return formattedBatchRow;
  }

  function generateSummaryRow(batchRows: TableBatchRow[]) {
    const summaryTasks = batchRows.reduce((acc, batchRow) => acc + batchRow.tasks, 0);
    const summaryHold = batchRows.reduce((acc, batchRow) => acc + batchRow.hold, 0);
    const summaryTasksCompleted = batchRows.reduce(
      (acc, batchRow) => acc + batchRow.tasksCompleted,
      0,
    );
    const summaryStages: Record<string, number> = {};
    const knownProperties: string[] = ['hold', 'tasksCompleted', 'tasks', 'batchInfo'];
    const stageNames: string[] = Object.keys(batchRows[0].stages).filter(
      key => !knownProperties.includes(key),
    );

    for (const stageName of stageNames) {
      summaryStages[stageName] = formattedTableData.reduce(
        (acc, batchRow) => acc + batchRow.stages[stageName],
        0,
      );
    }
    const summaryBatchInfo = { batchName: 'All Batches', batchId: '---', batchPriority: undefined };
    const summaryRow: TableBatchRow = {
      stages: summaryStages,
      batchInfo: summaryBatchInfo,
      tasks: summaryTasks,
      hold: summaryHold,
      tasksCompleted: summaryTasksCompleted,
    };
    return summaryRow;
  }

  // Action handlers
  const handleUpdateBatchPriority = async () => {
    const updateBatchPriority = async (): Promise<string> => {
      try {
        const response = await apiFetch(null, `corp-api/sfu_batch_status/update_batch_priority`, {
          method: 'POST',
          body: {
            forcedInherit: isForcingInheritance,
            priority: priorityLevelForUpdate,
            batchId: batchIdForUpdate,
          },
        });
        return response;
      } catch (error) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        return Promise.reject(error);
      }
    };

    const message = await updateBatchPriority();
    alert(message);
  };

  const formattedTableData = batchOverview?.batchRows?.map?.(formatBatchRow) || [];
  // Add summary row
  if (formattedTableData.length > 0) {
    formattedTableData.push(generateSummaryRow(formattedTableData));
  }

  // Generate columns
  const stageColumns: TableColumn[] =
    batchOverview?.stageDefinitionsInfo.map((item: StageDefinitionInfo) =>
      generateStageColumn(item.stageDefinitionId, item.stageDefinitionName),
    ) ?? [];

  const TABLE_COLUMNS: TableColumn[] = [
    {
      header: 'Batch',
      id: 'batchName',
      cell: function batchCell(props: {
        row?: { original?: { batchInfo?: { batchId: string; batchName: string } } };
      }) {
        const { batchId, batchName } = props?.row?.original?.batchInfo || {
          batchId: '',
          batchName: '',
        };
        if (batchName !== 'All Batches') {
          return (
            <a className="text-info-700" href={`${toolUrl}/lookup/${batchId}`}>
              {batchName}
            </a>
          );
        } else {
          return 'All Batches';
        }
      },
    },
    { header: 'Tasks', id: 'tasks' },
    ...(stageColumns || []),
    { header: 'Hold', id: 'hold' },
    { header: 'Tasks Completed', id: 'tasksCompleted' },
    {
      id: 'priority',
      header: 'Priority',
      cell: function priorityCell(props: {
        row?: {
          original?: {
            batchInfo?: { batchId: string; batchName: string; batchPriority?: number | null };
          };
        };
      }) {
        const { batchName, batchId, batchPriority } = props?.row?.original?.batchInfo || {
          batchName: '',
          batchId: '',
          batchPriority: null,
        };
        if (batchName !== 'All Batches') {
          return (
            <a
              className="text-info-700"
              onClick={() => {
                setIsBatchPriorityModalOpen(true);
                setBatchIdForUpdate(batchId);
              }}
            >
              {batchPriority ?? 'Not Set'}
            </a>
          );
        } else {
          return '---';
        }
      },
    },
  ];
  return (
    <div className="border border-solid border-neutral-200 px-4 py-4 flex flex-col gap-2">
      <Typography.H3>Batch Overview</Typography.H3>
      {!formattedTableData ||
        (formattedTableData.length === 0 && (
          <div className="scaleui scale-tailwind">
            <Typography.Paragraph2>
              No batches have been created for this project. You can create a new batch in the{' '}
              <a className="text-info-900" href={`${toolUrl}/batch_actions`}>
                Batch Actions
              </a>{' '}
              tool.
            </Typography.Paragraph2>
          </div>
        ))}
      {formattedTableData.length > 0 && (
        <div className="py-1">
          <Table columns={TABLE_COLUMNS} data={formattedTableData} divideRows manualPagination />
        </div>
      )}
      {isBatchPriorityModalOpen && (
        <div className="scaleui scale-tailwind">
          <Modal
            className=""
            innerClassName="max-w-sm"
            onClose={() => {
              setIsBatchPriorityModalOpen(false);
            }}
            theme="scale-theme"
            title={`Set priority for Batch ${batchIdForUpdate}`}
            open={isBatchPriorityModalOpen}
          >
            <Typography.Paragraph2>
              Please be aware that if you manually update the priority level for the batch, all
              future tasks that are uploaded to this batch will be assigned the same priority level.
              To change individual task or substage priority, please use the SFU Task Tracker.
            </Typography.Paragraph2>
            <div>
              <TextField
                className="w-72 py-5"
                id="text-field"
                label="Updated Priority Level (10-50)"
                onChange={e => setPriorityLevelForUpdate(parseInt(e.target.value))}
                placeholder="10"
                error={priorityLevelForUpdate < 0 || priorityLevelForUpdate > 50}
                type="number"
                value={priorityLevelForUpdate}
              />
            </div>
            <div>
              <SelectMenu
                className="w-48"
                helperText="Please select whether or not to force all tasks and their respective substages in the batch to this specific priority level."
                label="Force inherit priority to tasks"
                options={[
                  {
                    id: 'Yes',
                    name: 'Yes',
                  },
                  {
                    id: 'No',
                    name: 'No',
                  },
                ]}
                selectedOptionId={isForcingInheritance ? 'Yes' : 'No'}
                setSelected={value => setIsForcingInheritance(value === 'Yes')}
              />
            </div>
            <Button
              onClick={() => {
                handleUpdateBatchPriority();
                setIsBatchPriorityModalOpen(false);
                setPriorityLevelForUpdate(0);
                setIsForcingInheritance(false);
                setBatchIdForUpdate('');
              }}
            >
              Submit
            </Button>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default SfuBatchStatusPanel;
